import { darkenToContrast } from "utils/accessibility";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Radio,
  RadioGroup,
  FormControlLabel,
  Divider,
} from "@mui/material";
import CustomStepper from "components/CustomStepper";
import bulkTokens from "../BulkSendSigners.tokens";
import UnlimitedLabel from "global/components/UnlimitedLabel/UnlimitedLabel";
import Unauthorized from "screens/Shared/Unauthorized";
import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import viewpaths from "global/constants/ViewPathConstants";
import history from "utils/history";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import CloseIcon from "@mui/icons-material/Close";
import {
  selectScreen,
  setChipLoader,
  setErrors,
  setIsRequest,
  setLoading,
  setReduxSelectedFile,
  setReduxSelectedParticipant,
  setReduxSignRequest,
  setUploadedFileNames,
  updateSelfSignedFlag,
} from "redux/appSlice";
import {
  getExpirationDays,
  getIsAutoReminderConfig,
  getReminderConfig,
  completeSelfSignRequest,
  prepareSelfSignRequest,
  uploadSelfSignAttachment,
  sendSelfSignRequest,
  getAppPlanLimitByEntity,
} from "../SignRequestService";
import {
  addTemplate,
  fetchFolderTree,
  updateTemplate,
} from "screens/Templates/MyTemplatesService";
import {
  fieldColor,
  getContactSuggestion,
  handleDraftBtn,
  setIdOnEachField,
  validateField,
  validateAndNormalizeSignRequestFieldTypes,
} from "../SignRequestHelper";
import { resolveNeedReminders } from "screens/MySignRequests/MySignRequestHelper";
import {
  addFile,
  GetSaveTemplatePopups,
  initialState,
  setCoordinates,
  uploadFiles,
} from "screens/Shared/CommonHelper";
import {
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
  formatCount,
  isUnlimitedLimit,
} from "helpers/methods";
import { hasAccessTo } from "layouts/Config";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import {
  FileField,
  RequestFile,
  RequestParticipant,
  SignRequest,
  Reminders,
  PlanLimit,
} from "models/interfaces";
import { fetchAccountDetails } from "Services/CommonServices";
import { RootState } from "utils/store";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import CustomSendingLoader from "global/components/CustomSendingLoader/CustomSendingLoader";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import CustomMobileStepper from "components/CustomMobileStepper";
import {
  allHeldAttachments,
  attachmentFieldNames,
  clearHeldAttachments,
  hasHeldAttachments,
  pruneHeldAttachments,
} from "../selfSignAttachmentStore";
import UploadDocuments from "./UploadDocuments";
import SignRequestEditor from "./SignRequestEditor";
import MobileSignRequest from "../Mobile/MobileSignRequest";
import CustomConfirmationModal from "global/components/ConfirmationModal/CustomConfirmationModal";

interface SignRequestComponentProps {
  useTemplate?: boolean;
  newTemplate?: boolean;
  updateTemplate?: boolean;
  editDraft?: boolean;
  copyRequest?: boolean;
  updateRequest?: boolean;
}

const SelfSignRequestComponent: React.FC<SignRequestComponentProps> = (
  props,
) => {
  useTitle(title.SIGN_REQUEST);
  const dispatch = useAppDispatch();
  // Router state is a template / draft / copy to load — unless it only carries
  // the folder the user started from (`parentId`), which the mount effect
  // applies on its own.
  const routeState: any = history.location.state;
  const templateData: any =
    routeState && Object.keys(routeState).some((key) => key !== "parentId")
      ? routeState
      : undefined;
  const {
    signRequest,
    isRequest,
    contacts,
    errors,
    loading,
    assignExistingFields,
    isBulkSend,
  } = useAppSelector((state: RootState) => state.app);
  const { userName, email } = useAppSelector((state: RootState) => state.auth);
  const isMobile = useAppSelector(selectScreen);
  const delegationState = useFeatureState(FeatureKeys.ENVELOPE_DELEGATION);
  const isDelegationAvailable =
    delegationState.status === strings.FEATURE_STATUS_AVAILABLE;
  const reminderState = useFeatureState(FeatureKeys.REMINDER_AUTOMATIC);
  const isReminderAvailable =
    reminderState.status === strings.FEATURE_STATUS_AVAILABLE;
  const autoExpirationState = useFeatureState(
    FeatureKeys.ENVELOPE_AUTO_EXPIRATION,
  );
  const isAutoExpirationAvailable =
    autoExpirationState.status === strings.FEATURE_STATUS_AVAILABLE;
  const sendEmailState = useFeatureState(FeatureKeys.ENVELOPE_SEND_EMAIL);
  const isSendEmailAvailable =
    sendEmailState.status === strings.FEATURE_STATUS_AVAILABLE;
  const sendEmailDisabledTooltip =
    sendEmailState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((sendEmailState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : sendEmailState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : undefined;
  const [currentStep, setCurrentStep] = useState(0);
  const [loader, setLoader] = useState(false);
  const [folderTree, setFolderTree] = useState<any[]>([]);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  // Sealing a self-signed document runs long enough to warrant the explanatory
  // overlay rather than the bare `loading` spinner.
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  const [isSignTypeDisable, setIsSignTypeDisable] = useState<boolean>(true);
  const [accountDetails, setAccountDetails] = useState<any>(null);
  const [signTypeLoading, setSignTypeLoading] = useState<boolean>(false);

  const selfSignedStats = accountDetails?.appPlanLimitList?.find(
    (plan: PlanLimit) => plan.entity === strings.selfSignedEnveloped,
  );
  const CertifiedSignedStats = accountDetails?.appPlanLimitList?.find(
    (plan: any) => plan.entity === strings.notariusVerifiedEnvelope,
  );
  const selfSignRemaining =
    (selfSignedStats?.limit ?? 0) - (selfSignedStats?.current ?? 0);
  const certifiedRemaining =
    (CertifiedSignedStats?.limit ?? 0) - (CertifiedSignedStats?.current ?? 0);

  const isCertifiedDisabled = useMemo(() => {
    return isSignTypeDisable || !accountDetails || certifiedRemaining <= 0;
  }, [isSignTypeDisable, accountDetails, certifiedRemaining]);

  const isCertifiedNotPurchased = useMemo(() => {
    if (!accountDetails) return false;
    return !CertifiedSignedStats || (CertifiedSignedStats.limit ?? 0) <= 0;
  }, [accountDetails, CertifiedSignedStats]);

  const getAccountDetails = async () => {
    try {
      const accDetails = await fetchAccountDetails();
      setAccountDetails(accDetails);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const handleCertificateAccess = async () => {
    try {
      setSignTypeLoading(true);
      const [selfSigned, notariusVerified] = await Promise.all([
        getAppPlanLimitByEntity(strings.selfSignedEnveloped),
        getAppPlanLimitByEntity(strings.notariusVerifiedEnvelope),
      ]);
      const isNotariusVerifiedEntityPresent =
        notariusVerified.hasOwnProperty("entity") &&
        notariusVerified.hasOwnProperty("limit");

      if (isNotariusVerifiedEntityPresent && notariusVerified["limit"] > 0) {
        setIsSignTypeDisable(false);
      } else {
        // setIsSignTypeDisable(true);
        dispatch(updateSelfSignedFlag(true));
      }
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSignTypeLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      await Promise.all([handleCertificateAccess(), getAccountDetails()]);
    };
    init();
  }, []);

  const hasMyRequestsFetchPermission = useMemo(
    () => hasAccessTo(strings.resources.sign, strings.fetchPermission),
    [],
  );

  const ALL_STEPS = ["Upload Document", "Sign Request Editor"] as const;

  const mobileButtonSx = isMobile
    ? {
        "&.MuiButtonBase-root": {
          minWidth: "unset !important",
          minHeight: "42px !important",
          height: "42px !important",
          width: "auto !important",
          fontSize: "0.75rem",
          padding: "0.25rem 0.825rem",
        },
      }
    : {};

  const steps = useMemo(
    () =>
      ALL_STEPS.map((label, index) => ({
        label,
        isActive: currentStep === index,
        isCompleted: currentStep > index,
      })),
    [currentStep],
  );

  const resetState = useCallback(() => {
    clearHeldAttachments();
    dispatch(setReduxSignRequest(null));
    dispatch(setReduxSelectedParticipant(null));
    dispatch(setReduxSelectedFile(null));
  }, [dispatch]);

  const fetchFolderTreeData = useCallback(async () => {
    try {
      const foldersTree = await fetchFolderTree();
      setFolderTree(foldersTree);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  }, []);

  const getAccountConfigs = useCallback(async () => {
    try {
      dispatch(setLoading(true));

      const [isAutoReminder, expirationDays] = await Promise.all([
        getIsAutoReminderConfig(),
        getExpirationDays(),
      ]);

      dispatch(setReduxSignRequest({ needReminders: isAutoReminder }));

      const reminder: Reminders = isAutoReminder
        ? await getReminderConfig()
        : signRequest.reminders;

      const selfSigner = {
        id: 0,
        name: userName,
        email: email,
        order: 1,
        color: fieldColor[0],
        cc: false,
      };

      const updates = {
        reminders:
          isTruthy(reminder.daysBeforeFirstReminder) &&
          isTruthy(reminder.daysBetweenReminders)
            ? reminder
            : signRequest.reminders,
        expirationDays: isTruthy(expirationDays)
          ? expirationDays
          : signRequest.expirationDays,
        signers: [selfSigner],
      };

      dispatch(setReduxSignRequest(updates));
      dispatch(setReduxSelectedParticipant(selfSigner));
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      dispatch(setLoading(false));
    }
  }, [
    dispatch,
    signRequest.reminders,
    signRequest.expirationDays,
    userName,
    email,
  ]);

  const processTemplateData = (signRequestFromTemp: SignRequest) => {
    if (!signRequestFromTemp.approval) {
      signRequestFromTemp.approval = {
        approvalNeeded: false,
        orderNeeded: false,
        reviewers: [],
      };
    }

    if (signRequestFromTemp.approval.reviewers) {
      signRequestFromTemp.approval.reviewers =
        signRequestFromTemp.approval.reviewers.map((reviewer) => ({
          ...reviewer,
          color: reviewer.color
            ? darkenToContrast(reviewer.color)
            : fieldColor[reviewer.id],
        }));
    }
    if (signRequestFromTemp.files) {
      signRequestFromTemp.files = signRequestFromTemp.files.map(
        (file: any) => ({
          ...file,
          unsignedDoc: file.unsignedPdf || file.unsignedDoc,
          unsignedDocUrl: file.unsignedPdfUrl || file.unsignedDocUrl,
          isUploading: props.copyRequest ? true : file.isUploading || false,
        }),
      );
    }
    signRequestFromTemp.needReminders = resolveNeedReminders(signRequestFromTemp);
    if (signRequestFromTemp.signRequestConfig === null) {
      signRequestFromTemp.signRequestConfig = initialState.signRequestConfig;
    }
    // When updating an existing request that had a signer delegate to someone
    // else, drop the original (now-delegated) signer so the update form only
    // shows the actual current participants. Reset the `delegated` flag on the
    // remaining signers so the update payload doesn't carry stale delegation
    // state. Matches the behavior of copySignRequest.
    const sourceSigners =
      props.updateRequest && Array.isArray(signRequestFromTemp?.signers)
        ? signRequestFromTemp.signers
            .filter(
              (signer: RequestParticipant) =>
                signer.status !== strings.delegated,
            )
            .map((signer: RequestParticipant) => ({
              ...signer,
              delegated: false,
            }))
        : signRequestFromTemp?.signers || [];

    // When using a template, any per-envelope verification state on the
    // source (security code, pending OTP, verification result, failed-attempt
    // counter) must not be carried into the new request. Only the security
    // mode the requester picked (needSecuredAccess / needOTPValidation) is
    // kept so the dropdown selection persists. Matches copySignRequest.
    const shouldScrubSecurityCode = !!props.useTemplate;
    const scrubSecurity = (p: RequestParticipant): RequestParticipant =>
      shouldScrubSecurityCode && p.requestSecurity
        ? {
            ...p,
            requestSecurity: {
              ...p.requestSecurity,
              securityCode: null,
              otp: null,
              otpValidUntil: null,
              isVerified: false,
              verified: false,
              verifiedAt: null,
              failedAttempts: 0,
            },
          }
        : p;

    const signersWithColor = sourceSigners.map(
      (signer: RequestParticipant) => ({
        ...scrubSecurity(signer),
        color: fieldColor[signer.id],
      }),
    );
    signRequestFromTemp.signers = signersWithColor;

    if (shouldScrubSecurityCode && signRequestFromTemp.approval?.reviewers) {
      signRequestFromTemp.approval.reviewers =
        signRequestFromTemp.approval.reviewers.map(scrubSecurity);
    }

    if (props.useTemplate) {
      const tid = signRequestFromTemp.id;
      signRequestFromTemp.templateId =
        tid != null && String(tid).trim() !== "" ? String(tid) : null;
    } else if (props.copyRequest || props.updateTemplate) {
      signRequestFromTemp.templateId = null;
    } else {
      signRequestFromTemp.templateId = signRequestFromTemp.templateId ?? null;
    }

    return signRequestFromTemp;
  };

  const handleNext = useCallback(
    async (updateRequest: boolean = false) => {
      try {
        const { isValid: isCurrentStepValid, firstErrorMessage } =
          validateField(
            signRequest,
            errors,
            dispatch,
            props,
            steps[currentStep]?.label,
            isBulkSend,
            assignExistingFields,
            (props.newTemplate || props.updateTemplate)!,
          );

        if (!isCurrentStepValid) {
          if (firstErrorMessage) {
            openErrorNotification(firstErrorMessage);
          }
          return;
        }

        if (currentStep === steps.length - 1) {
          // Validate and normalize field types before sending/updating request
          // This will automatically fix types like "Yvette-date_signed-1" to "date_signed"

          const emptyRequiredFields: any[] = [];

          signRequest.files?.forEach((file: RequestFile) => {
            const fields = file.fields || [];

            // Normal fields
            fields.forEach((field: FileField) => {
              // A library field can also be satisfied by its default value: the server copies it
              // into `value` on send (applyDefaultValues), so an untouched default is not "empty".
              const filled =
                isTruthy(field.value) ||
                (isTruthy((field as any).customFieldId) &&
                  isTruthy(field.defaultValue));
              if (
                field.type !== strings.radioButton &&
                field.required &&
                !filled
              ) {
                emptyRequiredFields.push(field);
              }
            });

            // Radio groups
            const requiredRadioGroups = Array.from(
              new Set(
                fields
                  .filter(
                    (field) =>
                      field.type === strings.radioButton &&
                      field.required &&
                      field.groupId,
                  )
                  .map((field) => field.groupId as string),
              ),
            );

            requiredRadioGroups.forEach((groupId) => {
              const groupFields = fields.filter(
                (field) =>
                  field.type === strings.radioButton &&
                  field.groupId === groupId,
              );

              const hasSelection = groupFields.some(
                (field) => field.value === "true",
              );

              if (!hasSelection) {
                emptyRequiredFields.push({ type: "radio_group", groupId });
              }
            });
          });

          if (emptyRequiredFields.length > 0) {
            openErrorNotification(
              `Please fill all required fields before sending. ${emptyRequiredFields.length} required field(s) are empty.`,
            );
            return;
          }
          const validationResult =
            validateAndNormalizeSignRequestFieldTypes(signRequest);

          // Always use the normalized request (with corrected field types)
          const requestObject = setCoordinates(
            validationResult.normalizedRequest,
          );

          pruneHeldAttachments(attachmentFieldNames(requestObject?.files));

          if (!isDelegationAvailable && requestObject?.signRequestConfig) {
            requestObject.signRequestConfig = {
              ...requestObject.signRequestConfig,
              allowSignatureDelegation: false,
            };
          }

          if (!isReminderAvailable) {
            requestObject.needReminders = false;
            requestObject.reminders = null;
          }

          if (!isAutoExpirationAvailable) {
            delete (requestObject as any).expirationDays;
          }

          try {
            setIsSendingRequest(true);
            dispatch(setLoading(true));

            const signRequestObject = {
              ...requestObject,
              selfSigned: requestObject?.selfSigned,
              signers: [
                {
                  ...requestObject?.signers?.[0],
                  email,
                  name: userName,
                },
              ],
              files: requestObject.files?.map(
                ({
                  unsignedDoc,
                  unsignedDocUrl,
                  fields = [],
                  ...file
                }: any) => ({
                  ...file,
                  unsignedPdf: unsignedDoc,
                  unsignedPdfUrl: unsignedDocUrl,
                  fields: fields.map((field: any) => {
                    if (
                      ["signature", "initials"].includes(
                        field.type?.toLowerCase?.(),
                      )
                    ) {
                      const { value, ...restField } = field;
                      restField.value = "image";
                      return restField;
                    }

                    if (field.type?.toLowerCase?.() === strings.attachment) {
                      const { value, ...restField } = field;
                      restField.value = "";
                      return restField;
                    }

                    return field;
                  }),
                }),
              ),
            };

            if (hasHeldAttachments()) {
              const prepared = await prepareSelfSignRequest(signRequestObject);
              const requestToken = prepared?.requestToken;
              if (!requestToken) {
                throw new Error(
                  "Could not start the signing session for this request.",
                );
              }

              for (const { fieldName, items } of allHeldAttachments()) {
                for (const item of items) {
                  if (!item.file) continue;
                  // The sender's own authenticated endpoint, not the signer's guest one: no token or
                  // key travels to the browser for an envelope this user already owns.
                  await uploadSelfSignAttachment(
                    signRequestObject.id,
                    fieldName,
                    item.file,
                  );
                }
              }
              await completeSelfSignRequest(requestToken, prepared.files || []);
              clearHeldAttachments();
            } else {
              await sendSelfSignRequest(signRequestObject);
            }

            openSuccessNotification("Request sent successfully!");

            dispatch(setReduxSignRequest(null));
            history.push(
              `${viewpaths.viewRequestViewPath}/${signRequestObject?.id}`,
            );
          } finally {
            setIsSendingRequest(false);
            dispatch(setLoading(false));
          }
        } else {
          setCurrentStep(currentStep + 1);
        }
      } catch (error: any) {
        console.error(error);
        handleErrorMessage(error);
      }
    },
    [
      signRequest,
      errors,
      dispatch,
      props,
      currentStep,
      steps.length,
      isRequest,
      isDelegationAvailable,
      isReminderAvailable,
      isAutoExpirationAvailable,
    ],
  );

  const changeStep = useCallback(
    async (nextStep: number) => {
      let isNextStepValid = true;
      let firstErrorMessage: string | undefined;

      if (currentStep < nextStep) {
        for (let i = currentStep; i < nextStep; i++) {
          const result = validateField(
            signRequest,
            errors,
            dispatch,
            props,
            steps[i]?.label,
            isBulkSend,
            assignExistingFields,
            (props.newTemplate || props.updateTemplate)!,
          );
          isNextStepValid = result.isValid;
          if (!result.isValid && result.firstErrorMessage) {
            firstErrorMessage = result.firstErrorMessage;
            break;
          }
        }
      }
      if (isNextStepValid) {
        setCurrentStep(nextStep);
      } else if (firstErrorMessage) {
        openErrorNotification(firstErrorMessage);
      }
    },
    [
      signRequest,
      errors,
      dispatch,
      props,
      currentStep,
      steps.length,
      isRequest,
    ],
  );

  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      dispatch(setErrors({}));
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, dispatch]);

  const handleCancel = useCallback(() => {
    resetState();
    history.goBack();
  }, [resetState, history]);

  const handleCancelClick = useCallback(() => {
    if (!signRequest?.files?.length) {
      handleCancel();
      return;
    }
    setOpenConfirm(true);
  }, [signRequest, handleCancel]);

  // Reset stale request state on mount so re-entering the flow after a direct
  // URL change in the address bar starts with an empty form. Template/draft data
  // is loaded separately from history.location.state by the effect below.
  useEffect(() => {
    resetState();
    dispatch(updateSelfSignedFlag(!urls.PROD));
    // The folder the user started from (see SentRequestsFolderView), restored
    // after the reset so the request is saved under it, not the root.
    const stateParentId = (history.location.state as any)?.parentId;
    if (stateParentId != null) {
      dispatch(setReduxSignRequest({ parentId: stateParentId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(setErrors({}));
  }, [currentStep, dispatch]);

  useEffect(() => {
    if (!templateData) {
      getAccountConfigs();
      return;
    }

    const signRequestFromTemp = structuredClone({
      ...templateData,
      approval: templateData?.approval || {
        approvalNeeded: false,
        orderNeeded: false,
        reviewers: [],
      },
    }) as SignRequest;

    const processedData = processTemplateData(signRequestFromTemp);
    const envelopeId = processedData?.id;

    setIdOnEachField(processedData.files, props);
    dispatch(setReduxSignRequest(processedData));

    if (props.useTemplate || props.copyRequest) {
      if (props.copyRequest) {
        dispatch(setChipLoader(true));
      }
      uploadFiles(
        processedData,
        setIsFileUploaded,
        !props.useTemplate,
        true,
        dispatch,
        true,
      );
    } else if (props.updateTemplate || props.editDraft || props.updateRequest) {
      for (const file of processedData.files) {
        dispatch(setUploadedFileNames([file.unsignedDoc]));
        addFile(file, isRequest, dispatch, envelopeId, false);
      }
    }
  }, [templateData, props, dispatch, isRequest]);

  useEffect(() => {
    fetchFolderTreeData();
  }, [fetchFolderTreeData]);

  useEffect(() => {
    const isAccess = hasAccessTo(
      strings.resources.contacts,
      strings.fetchPermission,
    );
    if (contacts.length < 10 && isAccess) {
      getContactSuggestion("", dispatch);
    }
  }, [contacts.length, dispatch]);

  useEffect(() => {
    if (props.newTemplate || props.updateTemplate) {
      dispatch(setIsRequest(false));
    }
  }, [props.newTemplate, props.updateTemplate, dispatch]);
  const isEditorStep = steps[currentStep]?.label === "Sign Request Editor";

  const getStepContent = () => {
    const currentStepLabel = steps[currentStep]?.label;

    switch (currentStepLabel) {
      case "Upload Document":
        return (
          <UploadDocuments
            updateTemplate={!!templateData?.id}
            isNewTemplate={templateData?.newTemplate}
            bulk
          />
        );
      case "Sign Request Editor":
        return <SignRequestEditor setLoader={setLoader} />;
      default:
        return null;
    }
  };

  const isSignAccess = hasAccessTo(
    strings.resources.sign,
    strings.sendPermission,
  );
  const isTemplateAddAccess = hasAccessTo(
    strings.resources.template,
    strings.addPermission,
  );
  const isTemplateUpdateAccess = hasAccessTo(
    strings.resources.template,
    strings.updatePermission,
  );

  if (!hasMyRequestsFetchPermission && isRequest) {
    return <Unauthorized permissionDenied={hasMyRequestsFetchPermission} />;
  }

  const isButtonDisabled = () => {
    return (
      signRequest.files.some((file: RequestFile) => file.isUploading) ||
      loading ||
      loader
    );
  };

  return (
    <>
      <CustomLoader isLoading={loading && !isSendingRequest} />
      <CustomSendingLoader
        isLoading={isSendingRequest}
        title={strings.SELF_SIGN_REQUEST_TITLE}
        description={strings.SELF_SIGN_REQUEST_DESCRIPTION}
      />
      {isMobile ? (
        <MobileSignRequest
          flowProps={props}
          steps={steps}
          currentStep={currentStep}
          changeStep={changeStep}
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          onBack={handleCancel}
          menuItems={[]}
          continueLabel={
            currentStep === steps.length - 1 ? strings.sign : "Continue"
          }
          isContinueDisabled={isButtonDisabled()}
          selfSign
          selfSignMode={{
            basicRemaining: selfSignRemaining,
            certifiedRemaining: certifiedRemaining,
            certifiedDisabled: isCertifiedDisabled,
          }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            minHeight: {
              xs: "100vh",
              md: isEditorStep ? "auto" : "100vh",
            },
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              bgcolor: "background.paper",
              zIndex: 10,
              borderBottom: "1px solid",
              borderColor: "divider",
              py: 2,
            }}
          >
            <Grid
              container
              spacing={2}
              alignItems="center"
              px={isMobile ? 0 : 1}
            >
              <Grid item xs="auto">
                <Tooltip title="Cancel" arrow>
                  <IconButton
                    onClick={handleCancelClick}
                    sx={{
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Grid>

              <Grid item xs>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    px: { xs: 1, md: 2 },
                  }}
                >
                  {" "}
                  {isMobile ? (
                    <CustomMobileStepper
                      steps={steps}
                      currentStep={currentStep}
                      handleChangeStep={changeStep}
                    />
                  ) : (
                    <CustomStepper
                      steps={steps}
                      currentStep={currentStep}
                      handleChangeStep={changeStep}
                    />
                  )}
                </Box>
              </Grid>
              <Grid item xs="auto">
                {isMobile && (
                  <Box
                    id="mobile-stripe"
                    sx={{
                      position: "fixed",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "60px",
                      backdropFilter: "blur(8px)",
                      backgroundColor: (theme) =>
                        alpha(theme.palette.background.paper, 0.95),
                      borderTop: "1px solid rgba(0,0,0,0.08)",
                      zIndex: 9,
                      pointerEvents: "none",
                      boxShadow: "0px -4px 12px rgba(0, 0, 0, 0.08)",
                    }}
                  />
                )}

                <Stack direction="row" spacing={1.5} alignItems="center">
                  {currentStep > 0 && (
                    <Button
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      onClick={handlePrevious}
                      disabled={isButtonDisabled()}
                      sx={{
                        minWidth: "90px",
                        textTransform: "none",
                        fontWeight: 500,
                        ...(isMobile && {
                          fontSize: "0.875rem",
                          paddingX: "0.125rem",
                          position: "fixed",
                          bottom: 12,
                          left: 16,
                          zIndex: 10,
                          "&.MuiButtonBase-root": {
                            minWidth: "unset !important",
                            minHeight: "42px !important",
                            height: "42px !important",
                            width: "auto !important",
                            fontSize: "0.75rem",
                            padding: "0.25rem 0.825rem",
                          },
                        }),
                      }}
                    >
                      Previous
                    </Button>
                  )}
                  <Box
                    sx={{
                      ...(isMobile && {
                        position: "fixed",
                        bottom: 12,
                        right: 16,
                        zIndex: 10,
                      }),
                    }}
                  >
                    {(() => {
                      const isFinalSendStep =
                        currentStep === steps.length - 1 && isRequest;
                      const isSendGated =
                        isFinalSendStep && !isSendEmailAvailable;
                      const nextLabel =
                        currentStep === steps.length - 1
                          ? isRequest
                            ? props.updateRequest
                              ? strings.update
                              : strings.sign
                            : props.updateTemplate
                              ? strings.update
                              : strings.save
                          : strings.next;
                      const hasTemplateAccess = props.updateTemplate
                        ? isTemplateUpdateAccess
                        : isTemplateAddAccess;
                      // Preserve the access gating the split button applied:
                      // hide (not just disable) when the action isn't allowed.
                      const hideMainButton =
                        (nextLabel === strings.send && !isSignAccess) ||
                        (nextLabel === strings.save && !hasTemplateAccess) ||
                        (nextLabel === strings.update && !hasTemplateAccess);
                      if (hideMainButton) return null;
                      // Single signer has nothing to pick from the dropdown, so
                      // this is a plain contained button, not the split button.
                      const nextButton = (
                        <Button
                          variant="contained"
                          size={isMobile ? "small" : "medium"}
                          onClick={() => handleNext(props.updateRequest)}
                          disabled={
                            isButtonDisabled() ||
                            !signRequest.files ||
                            signRequest.files.length === 0 ||
                            isSendGated
                          }
                          sx={{
                            minWidth: "90px",
                            textTransform: "none",
                            fontWeight: 500,
                            ...mobileButtonSx,
                          }}
                        >
                          {nextLabel}
                        </Button>
                      );
                      return isSendGated && sendEmailDisabledTooltip ? (
                        <Tooltip title={sendEmailDisabledTooltip} arrow>
                          <span>{nextButton}</span>
                        </Tooltip>
                      ) : (
                        nextButton
                      );
                    })()}
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ width: "100%" }} pt={isEditorStep ? 0 : 2}>
            {getStepContent()}
            {currentStep === 0 && !urls.PROD && (
              <Box
                sx={{
                  // Align the eSign-mode row with the centered upload column
                  // (bulk design) above, instead of the full-width xl container.
                  maxWidth: bulkTokens.page.maxWidth,
                  mx: "auto",
                  px: "40px",
                  boxSizing: "border-box",
                  mt: -8,
                  pb: isMobile ? 8 : 0,
                }}
              >
                <Box sx={{ mt: 1 }}>
                  <Divider sx={{ my: 1 }} />
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.primary"
                    >
                      Select eSign Mode:
                    </Typography>
                    <RadioGroup
                      row
                      aria-label="Select eSign mode"
                      value={signRequest.selfSigned ? "basic" : "certified"}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "certified" && isCertifiedDisabled) return;
                        dispatch(updateSelfSignedFlag(val === "basic"));
                      }}
                      sx={{ gap: 1 }}
                    >
                      <FormControlLabel
                        value="basic"
                        sx={{ m: 0 }}
                        control={<Radio color="primary" size="small" />}
                        label={
                          <Typography variant="body2" color="text.primary">
                            Basic eSign{" "}
                            <Typography
                              component="span"
                              variant="caption"
                              color="primary.main"
                              fontWeight={600}
                            >
                              ({formatCount(selfSignRemaining)} left)
                            </Typography>
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="certified"
                        sx={{ m: 0, opacity: isCertifiedDisabled ? 0.6 : 1 }}
                        control={
                          <Radio
                            color="primary"
                            size="small"
                            disabled={isCertifiedDisabled}
                          />
                        }
                        label={
                          <Typography variant="body2" color="text.primary">
                            Certified eSign{" "}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              fontWeight={600}
                            >
                              (
                              {isUnlimitedLimit(CertifiedSignedStats?.limit) ? (
                                <UnlimitedLabel iconSize={13} />
                              ) : (
                                `${formatCount(certifiedRemaining)} left`
                              )}
                              )
                            </Typography>
                          </Typography>
                        }
                      />
                    </RadioGroup>
                    {isCertifiedNotPurchased && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          history.push(
                            `${viewpaths.viewMyPlanViewPath}?tab=account-usage`,
                          );
                        }}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          color: "primary.main",
                          p: 0,
                          minWidth: "auto",
                          "&:hover": {
                            textDecoration: "underline",
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        Upgrade to unlock
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {/* ) : (
        <DesktopAppPrompt />
      )} */}
      <CustomConfirmationModal
        confirmationTitle="Leave without saving?"
        confirmationMessage="Are you sure you want to leave? Your unsaved changes will be lost."
        closeButtonName="Stay"
        confirmationFooterButtonName="Leave"
        open={openConfirm}
        handleButtonCall={() => {
          setOpenConfirm(false);
          handleCancel();
        }}
        onClose={() => {
          setOpenConfirm(false);
        }}
      />
    </>
  );
};

export default SelfSignRequestComponent;
